/**
 * Shared search-filter parsing and backend mapping.
 *
 * Two consumers:
 *   1. /search (`app/(protected)/search/page.tsx`) feeds URL search params
 *      through `parseSearchURLParams` then `buildBackendFilters`.
 *   2. /listen/[slug] (`app/(public)/listen/[slug]/page.tsx`) constructs a
 *      `ParsedSearchParams` directly from a config entry and runs the same
 *      `buildBackendFilters`.
 *
 * SSR and client searches share the interval payload builder. Legacy financial
 * bucket IDs are accepted at boundaries only; age retains discrete spans.
 */

import { z } from 'zod';
import type { FilterParams } from '@/lib/search-filter-params';
import {
  emptyFilterParams,
  FILTER_KEYS,
  filterDef,
  TRI_STATE_KEYS,
  type FilterKey,
  type FilterKind,
  type FilterURLParams,
} from '@/lib/search-filter-defs';
import { localizePathname, type Locale } from '@/lib/i18n-routes';
import type { CompanySearchParams } from '@/scripts/search-queries';

/** UI preview state must never form part of a search identity or its filters. */
export function searchParamsWithoutPreview(params: { toString(): string }): URLSearchParams {
  const search = new URLSearchParams(params.toString());
  search.delete('preview');
  return search;
}

/** Retired sorts in URLs and saved searches behave like an absent sort. */
export function normalizeSearchSort(sort: string, keyword = ''): string {
  if (!sort || /^(city|bundesland|industry|cpv)(-|$)/.test(sort)) {
    return keyword ? 'relevance' : 'revenue-desc';
  }
  return sort;
}

/**
 * What `/search` reads from its URL: the filters plus the paging that shapes
 * the result page. Same field names as `FilterParams` — the SSR path used to
 * rename four of them (`cities`, `bundesland`, `naceCodeParams`,
 * `industryParams`), which every consumer then had to rename back.
 */
export type ParsedSearchParams = FilterParams & {
  offset: number;
  sort: string;
  limit: number;
};

/* ────────────────────────── Range definitions ────────────────────────── */

/** A selectable bucket in one of the range filters. `null` means unbounded. */
export interface RangeDefinition {
  id: string;
  min: number | null;
  max: number | null;
}

/**
 * Legacy financial buckets, retained for URL, saved-search and SEO conversion.
 *
 * These used to exist in three places — here (SSR + /listen pages),
 * SearchPageClient (client-side re-search) and CompanySearchSidebar (what
 * the user picks) — and they had drifted: the sidebar offered a
 * `10m-50m` profit bucket that the two mappers split into `10m-20m` /
 * `20m-50m`. An id present in the picker but absent from the mapper
 * resolves to no bounds at all, so choosing that bucket silently applied
 * *no profit filter* and returned the unfiltered set. `lib/` owns them
 * now; the four financial tables are used only to convert old selections.
 */
export const FINANCIAL_RANGES: RangeDefinition[] = [
  { id: '0-100k', min: 0, max: 100000 },
  { id: '100k-1m', min: 100000, max: 1000000 },
  { id: '1m-5m', min: 1000000, max: 5000000 },
  { id: '5m-10m', min: 5000000, max: 10000000 },
  { id: '10m-20m', min: 10000000, max: 20000000 },
  { id: '20m-50m', min: 20000000, max: 50000000 },
  { id: '50m-100m', min: 50000000, max: 100000000 },
  { id: '100m-1b', min: 100000000, max: 1000000000 },
  { id: '1b+', min: 1000000000, max: null },
];

export const PROFIT_RANGES: RangeDefinition[] = [
  { id: 'loss-100m+', min: null, max: -100000000 },
  { id: 'loss-10m-100m', min: -100000000, max: -10000000 },
  { id: 'loss-1m-10m', min: -10000000, max: -1000000 },
  { id: 'loss-100k-1m', min: -1000000, max: -100000 },
  { id: 'loss-0-100k', min: -100000, max: 0 },
  { id: '0-100k', min: 0, max: 100000 },
  { id: '100k-1m', min: 100000, max: 1000000 },
  { id: '1m-5m', min: 1000000, max: 5000000 },
  { id: '5m-10m', min: 5000000, max: 10000000 },
  { id: '10m-50m', min: 10000000, max: 50000000 },
  { id: '50m-100m', min: 50000000, max: 100000000 },
  { id: '100m+', min: 100000000, max: null },
];

/**
 * Bilanzsumme (balance-sheet total) buckets.
 *
 * Same ladder as revenue on purpose — the two sit side by side in the Size
 * & financials group and a user comparing them should not have to re-read
 * the scale. Coverage is the reason this filter exists: 822,065 companies
 * carry a balance-sheet total against 77,126 with revenue, because most
 * German filers publish a balance sheet without a full P&L.
 */
export const AKTIVA_RANGES: RangeDefinition[] = [
  { id: '0-100k', min: 0, max: 100000 },
  { id: '100k-1m', min: 100000, max: 1000000 },
  { id: '1m-5m', min: 1000000, max: 5000000 },
  { id: '5m-10m', min: 5000000, max: 10000000 },
  { id: '10m-20m', min: 10000000, max: 20000000 },
  { id: '20m-50m', min: 20000000, max: 50000000 },
  { id: '50m-100m', min: 50000000, max: 100000000 },
  { id: '100m-1b', min: 100000000, max: 1000000000 },
  { id: '1b+', min: 1000000000, max: null },
];

export const EMPLOYEE_RANGES: RangeDefinition[] = [
  { id: 'emp-0-10', min: 0, max: 10 },
  { id: 'emp-10-50', min: 10, max: 50 },
  { id: 'emp-50-100', min: 50, max: 100 },
  { id: 'emp-100-500', min: 100, max: 500 },
  { id: 'emp-500-1000', min: 500, max: 1000 },
  { id: 'emp-1000+', min: 1000, max: null },
];

/** Legacy financial buckets are envelopes now; age retains discrete spans. */
export function legacyRangesToBounds(ids: string[], table: readonly RangeDefinition[]): { min?: number; max?: number } {
  const known = table.filter((r) => ids.includes(r.id));
  if (!known.length) return {};
  return {
    ...(!known.some((r) => r.min === null) && { min: Math.min(...known.map((r) => r.min!)) }),
    ...(!known.some((r) => r.max === null) && { max: Math.max(...known.map((r) => r.max!)) }),
  };
}

export function parseBound(value: unknown): number | undefined {
  if (typeof value !== 'number' && typeof value !== 'string') return undefined;
  if (typeof value === 'string' && !value.trim()) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n) : undefined;
}

export function parseBounds(minValue: unknown, maxValue: unknown): { min?: number; max?: number } {
  const min = parseBound(minValue);
  const max = parseBound(maxValue);
  return min !== undefined && max !== undefined && min > max ? { min: max, max: min } : { min, max };
}

const LEGACY_METRICS = [
  ['revenue', 'revenueRanges', 'revenueRange', FINANCIAL_RANGES],
  ['aktiva', 'aktivaRanges', 'aktivaRange', AKTIVA_RANGES],
  ['profit', 'profitRanges', 'profitRange', PROFIT_RANGES],
  ['employees', 'employeeRanges', 'employeeRange', EMPLOYEE_RANGES],
] as const;

/**
 * One metric's interval from the URL. The new `<metric>Min`/`Max` params win
 * as a pair, including empty/invalid ones; otherwise the legacy bucket ids.
 * `null` when the URL carries neither (the caller's fallback applies).
 */
function metricURLBounds(params: FilterURLParams, metric: string): { min?: number; max?: number } | null {
  const [, , legacy, table] = LEGACY_METRICS.find(([m]) => m === metric)!;
  const minKey = `${metric}Min`;
  const maxKey = `${metric}Max`;
  if (params.has(minKey) || params.has(maxKey)) return parseBounds(params.get(minKey), params.get(maxKey));
  if (!params.has(legacy)) return null;
  return legacyRangesToBounds(params.getAll(legacy), table);
}

/** Optional legacy keys are only accepted at stored/config data boundaries. */
export interface LegacyFinancialFilters {
  revenueRanges?: string[];
  aktivaRanges?: string[];
  profitRanges?: string[];
  employeeRanges?: string[];
}

/**
 * Person-age buckets, expressed as the *birth-year* window they select —
 * the backing column is `mv_search.avg_person_birth_year`, so an older age
 * band maps to a lower year range. Derived from the current year at call
 * time rather than module load, so a long-lived process doesn't drift
 * across a New Year boundary.
 */
export function personAgeRanges(
  currentYear: number = new Date().getFullYear(),
): RangeDefinition[] {
  return [
    { id: 'age-20-30', min: currentYear - 30, max: currentYear - 20 },
    { id: 'age-30-40', min: currentYear - 40, max: currentYear - 30 },
    { id: 'age-40-50', min: currentYear - 50, max: currentYear - 40 },
    { id: 'age-50-60', min: currentYear - 60, max: currentYear - 50 },
    { id: 'age-60-70', min: currentYear - 70, max: currentYear - 60 },
    { id: 'age-70+', min: null, max: currentYear - 70 },
  ];
}

/**
 * Turn the selected bucket ids into the discrete spans the backend
 * OR-merges (`Range.spans`).
 *
 * This used to collapse the selection into a single {min, max} envelope,
 * which is only correct when the picked buckets happen to be adjacent.
 * Selecting "0 – 100k" and "1b+" produced `{min: 0, max: null}` — every
 * company with any revenue at all (2,365,440 rows on prod) rather than the
 * 6,395 in those two bands. Sending the bands separately removes the class
 * of bug rather than special-casing it. Financial sliders now deliberately
 * use an envelope when converting legacy IDs; only age still uses this path.
 */
export function getSpans(
  rangeIds: string[],
  ranges: readonly RangeDefinition[],
): { gte?: number; lte?: number }[] | undefined {
  if (rangeIds.length === 0) return undefined;
  const spans = ranges
    .filter((r) => rangeIds.includes(r.id))
    .filter((r) => r.min !== null || r.max !== null)
    .map((r) => ({
      ...(r.min !== null && { gte: r.min }),
      ...(r.max !== null && { lte: r.max }),
    }));
  return spans.length > 0 ? spans : undefined;
}

/**
 * A financial year counts as a filter only when it is a plausible year.
 *
 * The sidebar used to clear the picker by passing `0` as a "cleared"
 * sentinel. Zero survives every `!= null` / `!== undefined` check on the way
 * out, so removing the filter wrote `year=0` into the URL and asked the
 * backend for companies that filed for year zero — of which there are none.
 * Clearing now passes `undefined`; this guard is the second line of defence,
 * so links already shared with `year=0` recover instead of returning nothing.
 */
export function sanitizeFinancialYear(value: unknown): number | undefined {
  const n =
    typeof value === 'string' ? Number.parseInt(value, 10) : (value as number);
  if (!Number.isInteger(n)) return undefined;
  // Bundesanzeiger filings start in the 2000s; allow next year for early filers.
  if (n < 1900 || n > new Date().getFullYear() + 1) return undefined;
  return n;
}

/* ─────────────────────────── URL param helpers ─────────────────────────── */

/** Next's `searchParams` record as `FilterURLParams`. */
function recordURLParams(
  params: Record<string, string | string[] | undefined>,
): FilterURLParams {
  return {
    has: (key) => params[key] !== undefined,
    get: (key) => {
      const val = params[key];
      if (Array.isArray(val)) return val[0] || '';
      return val || '';
    },
    getAll: (key) => {
      const val = params[key];
      if (Array.isArray(val)) return val;
      if (val) return [val];
      return [];
    },
  };
}

/** A browser `URLSearchParams` as `FilterURLParams`. */
export function searchURLParams(params: URLSearchParams): FilterURLParams {
  return {
    has: (key) => params.has(key),
    get: (key) => params.get(key) || '',
    getAll: (key) => params.getAll(key),
  };
}

/**
 * One filter from the URL, or `fallback` when the URL does not set it.
 *
 * "Sets" depends on the kind: a flag or the country binds as soon as its
 * param is present (`?hasEmail=` turns a preset off), a tri-state only on the
 * literal "true"/"false" (a bare `?founderLed=` must not filter), text and
 * lists only when non-empty, an interval when either bound or its legacy
 * bucket param is present, the year only when it is a plausible year.
 */
function readURLFilter(key: FilterKey, params: FilterURLParams, fallback: unknown): unknown {
  const def = filterDef(key);
  if (def.read) return def.read(params, fallback as never);
  switch (def.kind) {
    case 'flag':
      return params.has(def.url) ? params.get(def.url) === 'true' : fallback;
    case 'triState': {
      const raw = params.get(def.url);
      return raw === 'true' ? true : raw === 'false' ? false : fallback;
    }
    case 'text':
      return params.get(def.url) || fallback;
    case 'list': {
      const all = params.getAll(def.url);
      return all.length > 0 ? all : fallback;
    }
    case 'year':
      return sanitizeFinancialYear(params.get(def.url)) ?? sanitizeFinancialYear(fallback);
    case 'bound': {
      const bounds = metricURLBounds(params, def.metric!);
      if (!bounds) return fallback;
      return key.endsWith('Min') ? bounds.min : bounds.max;
    }
  }
}

/**
 * Every filter from the URL: the URL's value where it sets one, otherwise
 * `fallback`'s (a /listen preset on the client, the empty default on SSR).
 */
export function readURLFilters(params: FilterURLParams, fallback: FilterParams = emptyFilterParams()): FilterParams {
  return Object.fromEntries(
    FILTER_KEYS.map((key) => [key, readURLFilter(key, params, fallback[key])]),
  ) as unknown as FilterParams;
}

/**
 * Parse Next.js `searchParams` into a typed, fully-defaulted shape.
 * The `sort` default mirrors the original /search behavior: when a keyword
 * query is present and no explicit sort is given, sort by relevance; otherwise
 * sort by revenue desc.
 */
export function parseSearchURLParams(
  params: Record<string, string | string[] | undefined>,
): ParsedSearchParams {
  const url = recordURLParams(params);
  const filters = readURLFilters(url);
  return {
    ...filters,
    offset: parseInt(url.get('offset') || '0', 10) || 0,
    sort: normalizeSearchSort(url.get('sort'), filters.q || filters.personName),
    limit: parseInt(url.get('limit') || '50', 10) || 50,
  };
}

/**
 * True when the parsed params represent the unfiltered default landing on
 * /search (no filters, page 1, default sort/limit). The original page short-
 * circuits SSR in this case to keep first paint instant.
 */
/** Keys of ParsedSearchParams that shape the result *page*, not the result
 *  set. Everything else counts as a filter. */
const DISPLAY_KEYS: ReadonlySet<string> = new Set(['offset', 'sort', 'limit']);

export function isDefaultSearch(p: ParsedSearchParams): boolean {
  // Derived over the parsed object rather than a hand-kept list: the list
  // had drifted — every filter added later (registerType, hasYearlyMetadata,
  // memberships, legalStatus, tenderRole, the founder tri-states, leiCode,
  // ustIdnr, hasWebsite, hasEmail, revenueIncludeNull) was missing, so a URL
  // carrying only one of them skipped SSR and the client mount fetch then
  // showed the *unfiltered* default list under a filtered URL.
  const hasFilters = Object.entries(p).some(([key, value]) => {
    if (DISPLAY_KEYS.has(key)) return false;
    // A selected negative tri-state is still a filter (unlike the ordinary
    // false booleans, which are the default for website/email flags).
    if (TRI_STATE_KEYS.has(key)) return value !== undefined;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'boolean') return value;
    return value != null && value !== '';
  });
  return (
    !hasFilters && p.offset === 0 && p.sort === 'revenue-desc' && p.limit === 50
  );
}

/**
 * Build the `CompanySearchParams` object passed to `searchCompanies()`.
 * The `backend: 'custom'` fields of FILTER_DEFS are mapped by hand here; every
 * other field goes through `genericBackendFilters`.
 */
export function buildBackendFilters(
  p: ParsedSearchParams,
  opts: { naceCodes?: readonly { code: string; level: number }[] } = {},
): CompanySearchParams {
  const naceCodes = opts.naceCodes ?? [];

  const rev = boundsToRange(p.revenueMin, p.revenueMax);
  const prof = boundsToRange(p.profitMin, p.profitMax);
  const akt = boundsToRange(p.aktivaMin, p.aktivaMax);
  const emp = boundsToRange(p.employeesMin, p.employeesMax);
  // Age bands go through the same discrete-span path — picking "20–30"
  // and "60–70" must mean those two cohorts, not the single 20-to-70
  // window a merged min/max would produce.
  const age = getSpans(p.ageRanges, personAgeRanges());

  const codeToLevel = new Map(naceCodes.map((c) => [c.code, c.level]));
  const naceSectors = p.naceCodes.filter(
    (code) => codeToLevel.get(code) === 1,
  );
  const naceSubsectors = p.naceCodes.filter(
    (code) => codeToLevel.get(code) !== 1,
  );

  // foundedAtFrom / foundedAtTo arrive as 4-digit year strings. Lower bound
  // becomes Jan 1, upper bound becomes Dec 31 so both ends are inclusive.
  const foundedAt: { gte?: string; lte?: string } = {};
  if (/^\d{4}$/.test(p.foundedAtFrom))
    foundedAt.gte = `${p.foundedAtFrom}-01-01`;
  if (/^\d{4}$/.test(p.foundedAtTo)) foundedAt.lte = `${p.foundedAtTo}-12-31`;

  return {
    city: p.city.join(',') || undefined,
    registerId: p.registerId,
    registerCourt: p.registerCourt,
    // The backend field is `euId` (exact eu_id lookup); `euid` stays the URL /
    // FilterParams name. Pydantic drops unknown keys, so `euid` here filtered nothing.
    euId: p.euid || undefined,
    Bundesland: p.Bundesland,
    limit: p.limit,
    offset: p.offset,
    sort: p.sort,
    // `includeNull` is opt-in and only offered for revenue — 97.4% of
    // companies have no revenue on file, so folding NULLs in silently
    // (which the backend used to do for every bucket up to 5M) turned a
    // narrow bucket into "match everything".
    ...(rev && { revenue: { ...rev, ...(p.revenueIncludeNull && { includeNull: true }) } }),
    ...(prof && { profit: prof }),
    ...(akt && { aktiva: akt }),
    ...(emp && { employeeCount: emp }),
    naceSectors,
    naceSubsectors,
    ...(sanitizeFinancialYear(p.financialYear) != null && {
      financialYear: p.financialYear,
    }),
    ...(Object.keys(foundedAt).length > 0 && { foundedAt }),
    ...(age && { avgBirthYear: { spans: age } }),
    ...genericBackendFilters(p),
  };
}

/** Whether a generic field is sent at all — empty means "no filter". */
function isBackendValueSet(kind: FilterKind, value: unknown): boolean {
  switch (kind) {
    case 'list':
      return (value as unknown[]).length > 0;
    case 'triState':
      return value !== undefined;
    default:
      return Boolean(value);
  }
}

/**
 * The request keys of every non-custom FILTER_DEFS entry: sent only when set,
 * a flag as `true`, a tri-state with its `false`, through `toBackend` if the
 * entry has one.
 */
function genericBackendFilters(f: FilterParams): Partial<CompanySearchParams> {
  const out: Record<string, unknown> = {};
  for (const key of FILTER_KEYS) {
    const def = filterDef(key);
    const value = f[key];
    if (def.backend === 'custom' || !isBackendValueSet(def.kind, value)) continue;
    out[def.backend] = def.kind === 'flag' ? true : def.toBackend ? def.toBackend(value as never) : value;
  }
  return out as Partial<CompanySearchParams>;
}

/**
 * Zeroed defaults for `ParsedSearchParams`. Call this and override the
 * specific fields you need. Useful for /listen/[slug] which builds filters
 * from a config object rather than URL params.
 */
export function emptyParsedSearchParams(): ParsedSearchParams {
  return { ...emptyFilterParams(), offset: 0, sort: 'revenue-desc', limit: 50 };
}

/**
 * A filter object reduced to its meaning, for equality comparison.
 *
 * Two searches are the same search when they narrow the register the same
 * way — regardless of key order, of empty arrays versus absent keys, or of
 * `false` versus `undefined` on the boolean switches. That equivalence is
 * what lets `/search` tell the user "you already saved this" and stops the
 * same query being saved twice under two names.
 *
 * Deliberately *not* a hash: hashing would need the same canonicalisation on
 * the backend, and a second implementation of "is this the same search" is
 * exactly the kind of thing that drifts. A JSON compare against the searches
 * the client already fetched needs only this one function.
 */
export function canonicalFilters(filters: Record<string, unknown>): string {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(filters).sort()) {
    const value = filters[key];
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      // Selection order is not meaning: picking Bayern then Hessen is the
      // same search as Hessen then Bayern.
      out[key] = [...value].map(String).sort();
      continue;
    }
    // `false` on a switch means "not applied", the same as absent — but a
    // tri-state `false` (familyManaged=false) is a real filter, so only the
    // plain booleans that have no negative form are dropped.
    if (value === false && !TRI_STATE_KEYS.has(key)) continue;
    out[key] = value;
  }
  return JSON.stringify(out);
}

/** Interval payload, shared by SSR and client searches. */
export function boundsToRange(min?: number, max?: number): { gte?: number; lte?: number } | undefined {
  if (min === undefined && max === undefined) return undefined;
  return { ...(min !== undefined && { gte: min }), ...(max !== undefined && { lte: max }) };
}

/** Each kind's saved-search schema: a wrong type falls back to "no filter". */
const STORED_SCHEMA_BY_KIND: Record<FilterKind, () => z.ZodType> = {
  flag: () => z.boolean().catch(false),
  triState: () => z.boolean().optional().catch(undefined),
  text: () => z.string().catch(''),
  list: () => z.array(z.string()).catch([]),
  bound: () => z.preprocess(parseBound, z.number().optional()),
  year: () => z.preprocess(sanitizeFinancialYear, z.number().optional()),
};

const storedFilterSchema = z.object(
  Object.fromEntries(FILTER_KEYS.map((key) => [key, STORED_SCHEMA_BY_KIND[filterDef(key).kind]()])),
);

/** Normalize once when saved/legacy filters enter the app; never retain bucket keys. */
export function normalizeFilterParams(raw: unknown): FilterParams {
  const record = z.record(z.string(), z.unknown()).safeParse(raw);
  const input = record.success ? { ...record.data } : {};
  for (const [metric, legacy, , table] of LEGACY_METRICS) {
    const minKey = `${metric}Min` as const;
    const maxKey = `${metric}Max` as const;
    if (!(minKey in input) && !(maxKey in input)) {
      const ids = z.array(z.string()).safeParse(input[legacy]);
      const bounds = legacyRangesToBounds(ids.success ? ids.data : [], table);
      input[minKey] = bounds.min;
      input[maxKey] = bounds.max;
    }
    const bounds = parseBounds(input[minKey], input[maxKey]);
    input[minKey] = bounds.min;
    input[maxKey] = bounds.max;
    delete input[legacy];
  }
  return storedFilterSchema.parse(input) as unknown as FilterParams;
}

/**
 * One filter into the URL, if set. Lists repeat their param; a tri-state is
 * written as "true"/"false" because an omitted key and `false` mean different
 * things; the year is written only when it is a plausible year.
 */
function writeURLFilter(params: URLSearchParams, key: FilterKey, filters: FilterParams): void {
  const def = filterDef(key);
  if (def.write) return def.write(params, filters);
  const value = filters[key];
  switch (def.kind) {
    case 'list':
      for (const v of value as string[]) params.append(def.url, v);
      return;
    case 'flag':
      if (value) params.set(def.url, 'true');
      return;
    case 'year': {
      const year = sanitizeFinancialYear(value);
      if (year !== undefined) params.set(def.url, String(year));
      return;
    }
    case 'text':
      if (value) params.set(def.url, value as string);
      return;
    default: // triState, bound
      if (value !== undefined) params.set(def.url, String(value));
  }
}

/**
 * Filters → a `/search` URL.
 *
 * Exported because the saved-search cards need the same mapping to open a
 * stored filter set. A second implementation is exactly how the three range
 * tables drifted apart — the param names here (`naceCode` for `naceCodes`,
 * `person_name` for `personName`, …) are not derivable from the field names
 * and must not be re-guessed elsewhere.
 */
export function filtersToUrl(
  filters: FilterParams,
  overrides: {
    sort: string;
    offset: number;
    limit: number;
  },
  locale?: string,
): string {
  const params = new URLSearchParams();
  params.set('offset', overrides.offset.toString());
  params.set('sort', overrides.sort);
  params.set('limit', overrides.limit.toString());
  for (const key of FILTER_KEYS) writeURLFilter(params, key, filters);

  const basePath = locale ? localizePathname('/search', locale as Locale) : '/search';
  return `${basePath}?${params.toString()}`;
}
